/**
 * 防止屏幕自动锁定（Screen Wake Lock）。
 *
 * ⚠️ 先分清两个都叫"锁屏"的需求：
 * - **锁屏显示通知** → Web Push + Notification + 系统通知权限，和这里无关。
 * - **防止屏幕自动锁定** → 就是这个 API，而且**只在页面可见时有效**。
 *   它不能让应用在设备锁屏后继续跑代码，任何"后台持续执行"的需求都到此为止。
 *
 * 实现要点：锁会在**页面不可见时被系统自动释放**，而且不会有任何提示。切出去再切回来，
 * 屏幕就又开始自动熄灭了。所以必须在 visibilitychange 里重新申请——这一条不做，
 * 功能在真机上就是"一开始有用，用着用着没了"。
 */
import { failFrom, ok } from './result.js';
export function wakeLockSupported() {
    if (typeof navigator === 'undefined')
        return false;
    return typeof navigator.wakeLock?.request === 'function';
}
export function createScreenWakeLock(options = {}) {
    let sentinel = null;
    let wanted = false;
    let listening = false;
    async function request() {
        if (!wakeLockSupported())
            return { ok: false, reason: 'unsupported' };
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
            // 页面不可见时申请必然被拒。不算错误，等回到前台自动补申请。
            return { ok: false, reason: 'platform-restriction', detail: '页面不可见时无法申请' };
        }
        try {
            const nav = navigator;
            const s = await nav.wakeLock.request('screen');
            sentinel = s;
            s.addEventListener('release', () => {
                // 系统收走了锁（切后台、低电量、系统策略）。still wanted 的话回前台会补申请。
                if (sentinel === s)
                    sentinel = null;
                options.onChange?.(false, 'auto-released');
            });
            options.onChange?.(true, 'acquired');
            return ok();
        }
        catch (e) {
            options.onChange?.(false, 'failed');
            return failFrom('failed', e);
        }
    }
    function ensureListener() {
        if (listening || typeof document === 'undefined')
            return;
        listening = true;
        document.addEventListener('visibilitychange', () => {
            if (!wanted)
                return;
            if (document.visibilityState === 'visible' && !sentinel)
                void request();
        });
    }
    return {
        async acquire() {
            wanted = true;
            ensureListener();
            if (sentinel && !sentinel.released)
                return ok();
            return request();
        },
        async release() {
            wanted = false;
            const s = sentinel;
            sentinel = null;
            if (s && !s.released)
                await s.release().catch(() => undefined);
            options.onChange?.(false, 'released');
        },
        active: () => !!sentinel && !sentinel.released,
    };
}
//# sourceMappingURL=wake-lock.js.map